import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import Icon from '../../../components/ui-components/Icon';
import { UndoOutlined } from '@ant-design/icons';
import { DashboardList } from './DashboardList';
import { VerifyResultsModal } from '../ApplicationsList/VerifyResultsModal';
import { FilterDashboard } from './FilterDashboard';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import {
	ApplicationSummary,
	ApplicationSummaryList,
	CandidateListData,
	CandidateScreeningProgressSummaryFilter,
	PagingConfig,
} from '../../../shared/VolunteerApplicationServiceTypes';

const { Text } = Typography;

interface DashboardPageProps {
	isFilterApplied: boolean;
	isLengthyLanguage: boolean;
	applicationsList: ApplicationSummaryList;
	paging: PagingConfig;
	isFilterModalOpen: boolean;
	handleResetFilters: () => void;
	dashboardData: CandidateListData[];
	selectedCardIndex: number | null;
	handleCardClick: (index: number) => void;
	handleFilterPopup: () => void;
	handleFilterModalClose: () => void;
	handleBackFunctionaryClick: () => void;
	handleEvaluateClick: (application: ApplicationSummary) => void;
	handleVerifyResultsClick: (application: ApplicationSummary) => void;
	overallCandidates: boolean;
	isZCUnpublishedRecords: boolean;
	openResultsModal: boolean;
	setOpenResultsModal: (open: boolean) => void;
	applicationToVerify: ApplicationSummary | undefined;
	dashboardFilter: CandidateScreeningProgressSummaryFilter;
	onPageChange: (page: number, pageSize: number) => void;
	onDashboardFilterChange: (
		value: CandidateScreeningProgressSummaryFilter
	) => void;
}

export const DashboardPage = ({
	isFilterApplied,
	isLengthyLanguage,
	applicationsList,
	paging,
	isFilterModalOpen,
	handleResetFilters,
	dashboardData,
	selectedCardIndex,
	handleCardClick,
	handleFilterPopup,
	handleFilterModalClose,
	handleBackFunctionaryClick,
	handleEvaluateClick,
	handleVerifyResultsClick,
	overallCandidates,
	isZCUnpublishedRecords,
	openResultsModal,
	setOpenResultsModal,
	applicationToVerify,
	dashboardFilter,
	onPageChange,
	onDashboardFilterChange,
}: DashboardPageProps) => {
	const { t } = useTranslation();

	return (
		<>
			<div className="md:hidden flex justify-center items-center pt-3 pb-1 relative">
				<Text className="font-sans font-bold text-lg mb-1">
					{t('adminDashboard')}
				</Text>
			</div>
			<div className="flex justify-between items-center pt-1 pb-3 px-5 md:px-5 md:py-5 relative">
				{/* Back Button - Desktop */}
				<Button
					type="primary"
					onClick={handleBackFunctionaryClick}
					className="hidden md:block font-bold text-sm"
				>
					<span className="flex gap-2">
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</span>
				</Button>

				{/* Back Button + Title - Mobile */}
				<div className="block sm:hidden">
					<a
						onClick={handleBackFunctionaryClick}
						className="relative top:0 md:top-[3px] mr-2 font-bold text-sm"
					>
						<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
					</a>
				</div>

				{/* Centered Title */}
				<Text
					className="hidden md:block font-sans font-bold text-xl mb-1 absolute left-1/2 transform -translate-x-1/2"
					data-testid="screening-dashboard-title"
				>
					{t('adminDashboard')}
				</Text>

				{/* Filter Button - Desktop */}
				<div
					className={`flex ${
						isLengthyLanguage ? 'flex-col-reverse gap-2' : 'flex-row'
					}`}
				>
					{isFilterApplied ? (
						<Button
							className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
					<Button
						className={`hidden md:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterApplied ? t('filterApplied') : t('filter')}
					</Button>
				</div>

				{/* Filter Icon - Mobile */}
				<Button
					className="block sm:hidden"
					variant="outlined"
					onClick={handleFilterPopup}
				>
					<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
				</Button>
			</div>
			<div className="flex flex-wrap gap-4 pt-2 pb-5 px-5 md:py-5 md:px-5">
				{dashboardData.map((data, index) => (
					<div
						key={index}
						className="flex-1 basis-[calc(50%-0.5rem)] max-w-[calc(50%-0.5rem)] lg:basis-[calc(25%-0.75rem)] lg:max-w-[calc(25%-0.75rem)]"
					>
						<div
							className={`rounded-lg border-solid px-2 md:px-4 py-2 h-full ${
								selectedCardIndex === index
									? 'border-primary-blue'
									: data.count !== 0
									? 'border-gray-200'
									: 'border-gray-300'
							} ${
								data.count !== 0
									? 'bg-card-bg md:bg-white cursor-pointer'
									: 'bg-gray-200 cursor-not-allowed'
							}`}
							onClick={
								data.count !== 0 ? () => handleCardClick(index) : undefined
							}
						>
							<div className="flex gap-3 md:gap-6 items-center min-h-[100px]">
								<div className="flex-shrink-0">
									<img
										src={data.imageSrc}
										className="w-[38px] md:w-[50px] h-[38px] md:h-[50px]"
									/>
								</div>

								<div className="flex flex-col justify-center">
									<Text
										className={`text-sm md:text-lg leading-normal font-bold m-0 ${
											data.count !== 0 ? 'text-black' : 'text-secondary-gray'
										}`}
									>
										{data.count}
									</Text>
									<Text
										className={`text-[12px] md:text-[16px] m-0 ${
											data.count !== 0 ? 'text-gray-600' : 'text-secondary-gray'
										}`}
									>
										{data.description}
									</Text>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
			<div className="pt-2 pb-5 px-5 md:py-5 md:px-5">
				<DashboardList
					data={applicationsList}
					onEvaluateClick={handleEvaluateClick}
					onVerifyResultsClick={handleVerifyResultsClick}
					onPageChange={onPageChange}
					pageConfig={paging}
					overAllCandidateStatus={overallCandidates}
					isZCUnpublishedRecords={isZCUnpublishedRecords}
				/>
				<VerifyResultsModal
					openModal={openResultsModal}
					onClose={() => setOpenResultsModal(false)}
					application={applicationToVerify}
				/>
			</div>
			<FilterDashboard
				open={isFilterModalOpen}
				onClose={handleFilterModalClose}
				filterValue={dashboardFilter}
				onApplicationFilterChange={onDashboardFilterChange}
			/>
		</>
	);
};
